import {
  Box,
  Flex,
  Heading,
  HStack,
  Image,
  Link,
  LinkBox,
  LinkOverlay,
  Spacer,
  VStack,
} from '@cardboard-ui/react';
import { SpaceContextCard } from 'apps/TenantApp/components/SpaceContextCard';
import { CardContent } from 'components/cards/Card';
import { ItemMenu } from 'components/Item/ItemMenu';
import React from 'react';
import { generatePath } from 'react-router-dom';
import { graphql, useFragment } from 'utils/graphClient';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';
import { CANONICAL_ITEM_PATH } from 'utils/routes';
import { InviteeOverview } from '../InviteeOverview/InviteeOverview';
import { RSVPButton } from '../RSVPButton';
import { DateTimeLabel } from './DateTimeLabel';
import { EventCard_event$key } from './__generated__/EventCard_event.graphql';
import { generateFallbackImage } from 'utils/generateFallbackImage';

const eventCard_event = graphql`
  fragment EventCard_event on Event {
    id
    ...DateTimeLabel_event
    name
    space {
      ...SpaceContextCard_space
      color {
        light
        dark
      }
    }
    start {
      date
    }
    end {
      date
      time
    }
    currentInvitation {
      id
      rsvp
      invitee {
        id
      }
    }
    ...InviteeOverview
    coverImage {
      url
    }
    ... on Node {
      ...ItemMenu_node
    }
  }
`;

export interface EventCardProps {
  event: EventCard_event$key;
  useCannonicalLink?: boolean;
  disableSpaceContext?: boolean;
}

export const EventCard = ({
  event: _event,
  useCannonicalLink,
  disableSpaceContext,
}: EventCardProps) => {
  const event = useFragment(eventCard_event, _event);
  const isMobile = useActiveLayout() !== Layout.DESKTOP;
  const currentUserInvitation = event.currentInvitation;
  const isStartDateAvailable = !!event.start?.date;
  const url = useCannonicalLink
    ? generatePath(CANONICAL_ITEM_PATH, { itemId: event.id })
    : event.id;

  const shouldRenderImage = !isMobile;
  return (
    <SpaceContextCard space={event.space} disableContext={disableSpaceContext}>
      <LinkBox
        sx={{
          // This is a manual fix of known bug (LinkBox doesn't elevate buttons on top of it) of chakra https://github.com/chakra-ui/chakra-ui/pull/5679
          'a[href]:not(.chakra-linkbox__overlay), abbr[title], button': {
            position: 'relative',
            zIndex: 2,
          },
        }}
      >
        <HStack alignItems="stretch" spacing={0}>
          {shouldRenderImage && (
            <Box flex={0.33} pos="relative">
              <Image
                pos="absolute"
                h="full"
                w="full"
                objectFit="cover"
                src={event.coverImage?.url || generateFallbackImage(event.id)}
              />
            </Box>
          )}

          <Flex flex={!shouldRenderImage ? 1 : 0.67}>
            <CardContent>
              <VStack align="initial">
                <HStack spacing={1}>
                  <LinkOverlay as={Link} to={url}>
                    <Heading fontSize="lg" noOfLines={1}>
                      {event.name}
                    </Heading>
                  </LinkOverlay>
                  <Spacer />
                  <ItemMenu node={event} mode="grid-item" />
                </HStack>
                <span>
                  <DateTimeLabel event={event} />
                </span>
                <Spacer />
                <HStack>
                  <HStack>
                    <InviteeOverview invitations={event} canInvite={false} />
                  </HStack>
                  <Spacer />
                  {currentUserInvitation && (
                    <RSVPButton
                      initialStatus={currentUserInvitation.rsvp}
                      invitationId={currentUserInvitation.id}
                      hidden={!isStartDateAvailable}
                      size="xs"
                    />
                  )}
                </HStack>
              </VStack>
            </CardContent>
          </Flex>
        </HStack>
      </LinkBox>
    </SpaceContextCard>
  );
};
